import React, { useState, useRef, useEffect } from "react";
import "./table.less";
import service from "../../../services/service";
import { Invit } from "../../../types/invit.types";
import { formatDisplayValue, wrapText } from "../../../utils/financialHelper";
import { useParameters } from "../../../context/parametersContext";

interface FinancialData {
  metric: string;
  isSubMetric?: boolean;
  values: { [key: string]: string | number | boolean };
}

interface InvitData {
  name: string;
  color: string;
  data: FinancialData[];
}

const InvitsFinancialTable: React.FC = () => {
  const [selectedInvits, setSelectedInvits] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>([]);
  const [showQuarterDropdown, setShowQuarterDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showAddInvitModal, setShowAddInvitModal] = useState(false);
  const [allInvits, setAllInvits] = useState<string[]>([]);
  const [invitsData, setInvitsData] = useState<{ [key: string]: InvitData }>(
    {}
  );
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableQuartersByYear, setAvailableQuartersByYear] = useState<{
    [key: string]: string[];
  }>({});
  const [loading, setLoading] = useState(true);

  const addInvitModalRef = useRef<HTMLDivElement>(null);
  const quarterDropdownRef = useRef<HTMLDivElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);

  const { activeToggle } = useParameters();

  useEffect(() => {
    fetchInvitsData();
  }, [activeToggle]);

  const fetchInvitsData = async () => {
    try {
      setLoading(true);

      const isPublic = activeToggle === "Public";

      console.log("Fetching invits with isPublic =", isPublic);

      const response = await service.getInvits(isPublic);
      const invits: Invit[] = response.data || response;

      processInvitsData(invits);
    } catch (error) {
      console.error("Error fetching InvITs data:", error);
    } finally {
      setLoading(false);
    }
  };

  const processInvitsData = (invits: Invit[]) => {
    const invitNames = invits.map((invit) => invit.name);
    setAllInvits(invitNames);

    const processedData: { [key: string]: InvitData } = {};
    const yearsSet = new Set<string>();
    const quartersByYear: { [key: string]: Set<string> } = {};

    invits.forEach((invit, index) => {
      const financialMetrics = invit.financialMetrics || [];

      financialMetrics.forEach((yearData) => {
        const year = yearData.year;
        yearsSet.add(year);

        if (!quartersByYear[year]) {
          quartersByYear[year] = new Set<string>();
        }

        yearData.quarters?.forEach((quarter) => {
          const q = quarter.quarter.trim().toUpperCase();
          quartersByYear[year].add(q);
        });
      });

      console.log("quarters by year : ", quartersByYear);

      const metricsData = extractMetricsData(invit);

      processedData[invit.name] = {
        name: invit.name,
        color: getInvitColor(index),
        data: metricsData,
      };
    });

    const sortedYears = Array.from(yearsSet).sort((a, b) => {
      const extractYear = (str: string) => {
        const match = str.match(/\d{4}/);
        return match ? parseInt(match[0]) : 0;
      };
      return extractYear(b) - extractYear(a);
    });

    const quartersMap: { [key: string]: string[] } = {};
    Object.keys(quartersByYear).forEach((year) => {
      quartersMap[year] = Array.from(quartersByYear[year]).sort((a, b) => {
        const qNumA = parseInt(a.replace("Q", ""));
        const qNumB = parseInt(b.replace("Q", ""));
        return qNumA - qNumB;
      });
    });

    setAvailableYears(sortedYears);
    setAvailableQuartersByYear(quartersMap);
    setInvitsData(processedData);

    if (invitNames.length > 0) {
      setSelectedInvits(invitNames.slice(0, Math.min(2, invitNames.length)));
    }

    if (sortedYears.length > 0) {
      const firstYear = sortedYears[0];
      setSelectedYear(firstYear);

      if (quartersMap[firstYear] && quartersMap[firstYear].length > 0) {
        setSelectedQuarters(
          quartersMap[firstYear].slice(
            0,
            Math.min(2, quartersMap[firstYear].length)
          )
        );
      }
    }
  };

  const getInvitColor = (index: number): string => {
    return "#2FBEB7";
  };

  const extractMetricsData = (invit: Invit): FinancialData[] => {
    const metrics: FinancialData[] = [
      { metric: "Total Revenue from Operations", values: {} },
      { metric: "Sector / Sub-sector wise Revenue Split", values: {} },
      { metric: "EBITDA", values: {} },
      { metric: "EBITDA Margin (%)", values: {} },
      { metric: "O&M Expenses", values: {} },
      { metric: "Capex", values: {} },
      {
        metric: "Assets Under Management (AUM) or Enterprise Value (EV)",
        values: {},
      },
      { metric: "Market Capitalisation", values: {} },
      { metric: "NAV/ unit (Fair Value) (₹ p.u.)", values: {} },
      { metric: "NDCF of InvIT (Quarterly)", values: {} },
      { metric: "NDCF of InvIT (YTD)", values: {} },
      { metric: "Gross Borrowing", values: {} },
      { metric: "Cost of Debt (% p.a.)", values: {} },
      { metric: "Equity Raised Till Date", values: {} },
      { metric: "Credit Rating (from all agencies)", values: {} },
      {
        metric: "Total Distribution per Unit (Quarterly) (₹ p.u.)",
        values: {},
      },
      { metric: "Total Distribution per Unit (YTD) (₹ p.u.)", values: {} },
      { metric: "Net Debt/ AUM", values: {} },
    ];

    const getValue = (value: string | number | boolean | null | undefined) => {
      return typeof value === "boolean" ? (value ? "Yes" : "No") : value;
    };

    invit.financialMetrics?.forEach((yearData) => {
      const fiscalYear = yearData.year;

      yearData.quarters?.forEach((quarter) => {
        const q = quarter.quarter;
        const key = `${fiscalYear} ${q}`;

        metrics[0].values[key] = formatDisplayValue(
          getValue(quarter.totalRevenueFromOperations?.value)
        );
        metrics[1].values[key] =
          quarter.sectorOrSubSectorWiseRevenueSplit?.value || "—";
        metrics[2].values[key] = formatDisplayValue(
          getValue(quarter.ebitda?.value)
        );
        metrics[3].values[key] = formatDisplayValue(
          getValue(quarter.ebitdaMargin?.value)
        );
        metrics[4].values[key] = formatDisplayValue(
          getValue(quarter.operationsOandMexpenses?.value)
        );
        metrics[5].values[key] = formatDisplayValue(
          getValue(quarter.capex?.value)
        );
        metrics[6].values[key] = formatDisplayValue(
          getValue(quarter.assetsUnderManagementOrEnterpriseValue?.value)
        );
        metrics[7].values[key] = formatDisplayValue(
          getValue(quarter.marketCapitalisation?.value)
        );
        metrics[8].values[key] = formatDisplayValue(
          getValue(quarter.nav?.value)
        );
        metrics[9].values[key] = formatDisplayValue(
          getValue(quarter.ndfcAtInvitQuarterly?.value)
        );
        metrics[10].values[key] = formatDisplayValue(
          getValue(quarter.ndcfAtInvitYTD?.value)
        );
        metrics[11].values[key] = formatDisplayValue(
          getValue(quarter.grossBorrowing?.value)
        );
        metrics[12].values[key] = formatDisplayValue(
          getValue(quarter.costOfDebt?.value)
        );
        metrics[13].values[key] = formatDisplayValue(
          getValue(quarter.equityRaisedTillDate?.value)
        );
        metrics[14].values[key] = quarter.creditRating?.value || "—";
        metrics[15].values[key] = formatDisplayValue(
          getValue(quarter.distributionPerUnitQuarterly?.value)
        );
        metrics[16].values[key] = formatDisplayValue(
          getValue(quarter.distributionPerUnitYTD?.value)
        );
        metrics[17].values[key] = formatDisplayValue(
          getValue(quarter.netDebtOrAum?.value)
        );
      });
    });

    return metrics;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        quarterDropdownRef.current &&
        !quarterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowQuarterDropdown(false);
      }
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target as Node)
      ) {
        setShowYearDropdown(false);
      }
      if (
        addInvitModalRef.current &&
        !addInvitModalRef.current.contains(event.target as Node)
      ) {
        setShowAddInvitModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedYear && availableQuartersByYear[selectedYear]) {
      const quarters = availableQuartersByYear[selectedYear];
      if (quarters.length > 0 && selectedQuarters.length === 0) {
        setSelectedQuarters(quarters.slice(0, Math.min(2, quarters.length)));
      }
    }
  }, [selectedYear, availableQuartersByYear]);

  const metrics =
    invitsData[selectedInvits[0]]?.data.map((d) => d.metric) || [];

  const removeInvit = (invitName: string) => {
    if (selectedInvits.length > 1) {
      setSelectedInvits(selectedInvits.filter((name) => name !== invitName));
    }
  };

  const addInvit = (invitName: string) => {
    if (selectedInvits.length < 3 && !selectedInvits.includes(invitName)) {
      setSelectedInvits([...selectedInvits, invitName]);
      setShowAddInvitModal(false);
    }
  };

  const toggleQuarter = (quarter: string) => {
    if (selectedQuarters.includes(quarter)) {
      if (selectedQuarters.length > 1) {
        setSelectedQuarters(selectedQuarters.filter((q) => q !== quarter));
      }
    } else {
      if (selectedQuarters.length < 4) {
        const newQuarters = [...selectedQuarters, quarter].sort((a, b) => {
          const qNumA = parseInt(a.replace("Q", ""));
          const qNumB = parseInt(b.replace("Q", ""));
          return qNumA - qNumB;
        });
        setSelectedQuarters(newQuarters);
      }
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    const quarters = availableQuartersByYear[year] || [];
    if (quarters.length > 0) {
      setSelectedQuarters(quarters.slice(0, Math.min(2, quarters.length)));
    }
    setShowYearDropdown(false);
  };

  const periods = selectedQuarters.map((q) => `${selectedYear} ${q}`);

  const currentQuarters = selectedYear
    ? availableQuartersByYear[selectedYear] || []
    : [];

  if (loading) {
    return (
      <div className="financial-table">
        <div
          style={{
            textAlign: "center",
            padding: "50px",
            fontSize: "16px",
            color: "#667085",
          }}
        >
          Loading InvITs data...
        </div>
      </div>
    );
  }

  return (
    <div className="financial-table">
      <div className="financial-table__header-section">
        <div className="financial-table__title-section">
          <h3>
            Compare <br />
            <span>InvITs Financial Metrics</span>
          </h3>
        </div>
        <div className="financial-table__controls">
          <div className="financial-table__left-controls">
            <div
              className="financial-table__dropdown-wrapper"
              ref={quarterDropdownRef}
            >
              <button
                className="financial-table__selector-btn"
                onClick={() => {
                  setShowQuarterDropdown(!showQuarterDropdown);
                  setShowYearDropdown(false);
                }}
              >
                <span className="financial-table__btn-text">
                  {selectedQuarters.join(", ")}
                </span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showQuarterDropdown && (
                <div className="financial-table__dropdown">
                  {[
                    ...new Set(
                      currentQuarters.map((q) => q.trim().toUpperCase())
                    ),
                  ].map((quarter) => (
                    <label
                      key={quarter}
                      className="financial-table__dropdown-item"
                    >
                      <input
                        type="checkbox"
                        checked={selectedQuarters.includes(quarter)}
                        onChange={() => toggleQuarter(quarter)}
                        disabled={
                          !selectedQuarters.includes(quarter) &&
                          selectedQuarters.length >= 4
                        }
                      />
                      <span>{quarter}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div
              className="financial-table__dropdown-wrapper"
              ref={yearDropdownRef}
            >
              <button
                className="financial-table__selector-btn financial-table__selector-btn--year"
                onClick={() => {
                  setShowYearDropdown(!showYearDropdown);
                  setShowQuarterDropdown(false);
                }}
              >
                <span className="financial-table__btn-text">
                  {selectedYear}
                </span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {showYearDropdown && (
                <div className="financial-table__dropdown">
                  {availableYears.map((year) => (
                    <label
                      key={year}
                      className="financial-table__dropdown-item"
                    >
                      <input
                        type="radio"
                        checked={selectedYear === year}
                        onChange={() => handleYearChange(year)}
                      />
                      <span>{year}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div
              className="financial-table__add-invit-wrapper"
              ref={addInvitModalRef}
            >
              <button
                className="financial-table__add-invit-btn"
                onClick={() => setShowAddInvitModal(!showAddInvitModal)}
                disabled={selectedInvits.length >= 3}
              >
                <span className="financial-table__add-invit-icon">+</span>
              </button>
              <span className="financial-table__add-invit-text">
                Add InvITs
              </span>

              {showAddInvitModal && (
                <div className="financial-table__invit-modal">
                  <div className="financial-table__invit-modal-header">
                    <h3>Select InvIT</h3>
                    <p>Choose up to 3 InvITs to compare</p>
                  </div>
                  <div className="financial-table__invit-modal-body">
                    {allInvits.map((invit) => {
                      const isSelected = selectedInvits.includes(invit);
                      const invitColor = invitsData[invit]?.color || "#3CBFB3";

                      return (
                        <div
                          key={invit}
                          className={`financial-table__invit-card ${
                            isSelected
                              ? "financial-table__invit-card--selected"
                              : ""
                          } ${
                            !isSelected && selectedInvits.length >= 3
                              ? "financial-table__invit-card--disabled"
                              : ""
                          }`}
                          onClick={() => {
                            if (!isSelected && selectedInvits.length < 3) {
                              addInvit(invit);
                            }
                          }}
                          style={{
                            opacity: isSelected ? 0.5 : 1,
                            pointerEvents: isSelected ? "none" : "auto",
                          }}
                        >
                          <div
                            className="financial-table__invit-card-indicator"
                            style={{ backgroundColor: invitColor }}
                          />
                          <div className="financial-table__invit-card-content">
                            <span className="financial-table__invit-card-name">
                              {invit}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="financial-table__container">
        <table className="financial-table__table">
          <thead>
            <tr>
              <th className="financial-table__header-metric">
                Financial Metrics
              </th>
              {selectedInvits.map((invitName) => (
                <th
                  key={invitName}
                  className="financial-table__header-invit"
                  colSpan={periods.length}
                  style={{ backgroundColor: invitsData[invitName]?.color }}
                >
                  <div className="financial-table__invit-header">
                    <span>{invitName}</span>
                    {selectedInvits.length > 1 && (
                      <button
                        className="financial-table__remove-btn"
                        onClick={() => removeInvit(invitName)}
                        aria-label={`Remove ${invitName}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              <th className="financial-table__subheader"></th>
              {selectedInvits.map((invitName) =>
                periods.map((period) => (
                  <th
                    key={`${invitName}-${period}`}
                    className="financial-table__subheader"
                  >
                    {period.split(" ").pop()}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, index) => (
              <tr key={index} className="financial-table__row">
                <td className="financial-table__metric-name">{metric}</td>
                {selectedInvits.map((invitName) => {
                  const invitData = invitsData[invitName];
                  const metricData = invitData?.data.find(
                    (d) => d.metric === metric
                  );
                  return periods.map((period) => (
                    <td
                      key={`${invitName}-${period}`}
                      className="financial-table__value"
                    >
                      {wrapText(metricData?.values[period])}
                    </td>
                  ));
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvitsFinancialTable;
